import { Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { formatNumber } from "../../../../../../../../common/utils";
import { RefrigerantsConsumptionResultsDTO } from "../../../../../../../../datasources/api-client/data-contracts";

export const getRefrigerantsRows = (data: RefrigerantsConsumptionResultsDTO, t: TFunction) => {
	if (!data) return [];

	const safeNumber = (val: any): number => {
		if (val === null || val === undefined || val === "" || isNaN(Number(val))) {
			return 0;
		}
		return Number(val);
	};

	const rows = Object.entries(data.items).map(([key, value], index) => {
		return {
			id: index,
			type: t(`greenrouter/refrigerants/${value.refrigerant.label.toLowerCase()}`),
			consumption: {
				value: safeNumber(value?.consumption?.value.value),
				measure: value?.consumption?.value.measure,
			},
			emissions: {
				value: safeNumber(value.co2e.value),
				percent: safeNumber(value.co2e.percentage),
			},
		};
	});

	// Add total row
	rows.push({
		id: rows.length,
		type: t("greenrouter/refrigerants/total"),
		consumption: {
			value: safeNumber(data.totalConsumption.value),
			measure: "",
		},
		emissions: {
			value: safeNumber(data.totalCo2e?.value),
			percent: 0,
		},
	});

	return rows;
};

export const refrigerantsDataCols: GridColDef[] = [
	{
		field: "type",
		headerName: "refrigerant",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			const isTotal = params.row.type?.toLowerCase().includes("total");
			return (
				<Stack direction="row" alignItems="center" height="100%">
					<Typography variant="body1" fontWeight={isTotal ? 700 : 400}>
						{params.value}
					</Typography>
				</Stack>
			);
		},
	},
	{
		field: "consumption",
		headerName: "consumption",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			const { value } = params.row.consumption;
			return (
				<Stack spacing={1} height="100%" direction="row" justifyContent="center" alignItems="center">
					<Typography flex={1} textAlign={"right"} variant="body1" fontWeight={700}>
						{formatNumber(value, "decimal", 2, 2)}
					</Typography>
				</Stack>
			);
		},
	},
	{
		field: "emissions",
		headerName: "emissions",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			const { value, percent } = params.row.emissions;
			return (
				<Stack spacing={1} height="100%" direction="row" justifyContent="center" alignItems="center">
					<Typography flex={1} textAlign={"right"} variant="body1" fontWeight={700}>
						{formatNumber(value, "decimal", 2, 2)}
					</Typography>
					<Typography flex={1} variant="caption" color="text.secondary">
						({formatNumber(percent / 100, "percent", 2, 2)})
					</Typography>
				</Stack>
			);
		},
	},
];

export const getRefrigerantsColumns = (dataCol: GridColDef[], t: TFunction) =>
	dataCol.map((col, index) => {
		const label = col.headerName ? t(`greenrouter/refrigerants/${col.headerName}`) : "";
		const justify = index === 0 ? "start" : index === 1 ? "end" : "center";

		return {
			...col,
			headerName: label,
			renderHeader: () => {
				if (!label) {
					return null;
				}

				return (
					<Stack direction={"column"} width={"100%"}>
						<Stack
							direction="row"
							justifyContent={justify}
							sx={{ fontWeight: "bold" }}
							spacing={1}
							alignItems="baseline"
						>
							<Typography variant="subtitle1">{label}</Typography>
							<Typography variant="body2" color="text.secondary">
								{
									// measure for Emissions col
								}
								{index === 1 && "kg"}

								{index === 2 && "tCO2e"}
							</Typography>
						</Stack>
					</Stack>
				);
			},
		};
	});
